// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.comment-sources.spec.ts
import { ArrowSquareOut, BookOpen } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Preview } from "../../../components/Preview";
import { useGetKnowledgeDataRecord } from "../../../utils/queries/knowledge-db";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  knowledgeDatabaseRoute,
} from "../../settings/routes/knowledge-database/route";

export function KnowledgeRecordContextPanel({
  recordId,
  onClose,
}: {
  recordId: string;
  onClose?: () => void;
}) {
  const { t } = useTranslation("ui");
  const query = useGetKnowledgeDataRecord(recordId);

  if (query.isLoading) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center p-6"
        data-testid="knowledge-record-context-loading"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("knowledgeRecordContextPanel.loading")}
        </Text>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 p-4"
        data-testid="knowledge-record-context-error"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("knowledgeRecordContextPanel.loadError")}
        </Text>
        {onClose ? (
          <Button
            size="1"
            variant="soft"
            color="gray"
            onClick={onClose}
            data-testid="knowledge-record-context-back"
          >
            {t("knowledgeRecordContextPanel.backToContextAssets")}
          </Button>
        ) : null}
      </div>
    );
  }

  const record = query.data;
  const fields = record.data ?? [];
  // The legacy per-model deep link is gone with the knowledge refresh; land
  // on the Rules tab (the route's .catch() would do the same for stale links).
  const knowledgeDatabaseSearch = KNOWLEDGE_DATABASE_DEFAULT_SEARCH;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="knowledge-record-context-panel"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-puntt-neutral-gray-6 px-3 py-2">
        <Flex gap="2" align="center">
          <BookOpen size={16} className="text-puntt-accent-11" aria-hidden />
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            {t("knowledgeRecordContextPanel.header")}
          </Text>
        </Flex>
        <Flex gap="2" align="center">
          <Button size="1" variant="outline" asChild>
            <Link
              to={knowledgeDatabaseRoute.to}
              search={knowledgeDatabaseSearch}
              target="_blank"
              rel="noreferrer"
              className="puntt-link inline-flex items-center gap-1"
              data-testid="knowledge-record-context-knowledge-database-link"
            >
              <Text size="1">
                {t("knowledgeRecordContextPanel.knowledgeDatabaseLink")}
              </Text>
              <ArrowSquareOut size={12} aria-hidden />
            </Link>
          </Button>
          {onClose ? (
            <Button
              size="1"
              variant="soft"
              color="gray"
              onClick={onClose}
              data-testid="knowledge-record-context-back"
            >
              {t("knowledgeRecordContextPanel.back")}
            </Button>
          ) : null}
        </Flex>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="rounded-md border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3">
          <Preview knowledgeDataRecord={fields} />
        </div>
      </div>
    </div>
  );
}
